// =============================================================================
// DirectedQuestionCard — question row for the Inspeção Direcionada form
//
// Renders the question text, the three answer buttons (Atende / N/A / Não Atende)
// and — when "Não Atende" is selected — the action-plan sub-form.
// =============================================================================

import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import { Button } from '@/components/ui/button'
import { FieldContent } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { DirecionadaInput } from '@/schemas/direcionada-schema'
import {
	formatDateToDisplay,
	formatDateYMD,
	parseStringToDateForDisplay,
} from '@/utils/formatDate'
import { convertHeicIfNeeded } from '@/utils/image-compression'
import { INSPECTION_COLORS } from '../../constants/inspection-colors'
import { SearchableSelect } from '../ui/searchable-select'
import { InspectionDateField } from './inspection-date-field'
import { InspectionPhotoField } from './inspection-photo-field'

/* ────────────────────────────── Types ──────────────────────────────────── */

export type AnswerValue = 'COMPLIANT' | 'NON_COMPLIANT' | 'NOT_APPLICABLE'
export type StatusValue = 'IN_PROGRESS' | 'RESOLVED'

export interface QuestionState {
	answer: AnswerValue
	responsibleId?: string
	dueDate?: Date
	actionPlan?: string
	status?: StatusValue
}

export interface DirectedQuestion {
	id: string
	title: string
	imagePath?: string
}

export interface DirectedResponsible {
	id: string
	name: string
}

/* ────────────────────────────── Constants ──────────────────────────────── */

const ANSWER_OPTIONS: {
	value: AnswerValue
	labelKey: string
	selectedClass: string
}[] = [
	{
		value: 'COMPLIANT',
		labelKey: 'inspections.directed.answerCompliant',
		selectedClass:
			'bg-kaefer-grass-green text-white border-kaefer-grass-green hover:bg-kaefer-grass-green/90',
	},
	{
		value: 'NOT_APPLICABLE',
		labelKey: 'inspections.directed.answerNotApplicable',
		selectedClass:
			'bg-kaefer-silver text-white border-kaefer-silver hover:bg-kaefer-silver/90',
	},
	{
		value: 'NON_COMPLIANT',
		labelKey: 'inspections.directed.answerDoesNotMeet',
		selectedClass:
			'bg-kaefer-red text-white hover:bg-kaefer-red/90 border-kaefer-red',
	},
]

const STATUS_OPTIONS: {
	value: Exclude<StatusValue, ''>
	labelKey: string
}[] = [
	{ value: 'IN_PROGRESS', labelKey: 'inspections.directed.statusInProgress' },
	{ value: 'RESOLVED', labelKey: 'inspections.directed.statusResolved' },
]

/* ────────────────────────────── Component ──────────────────────────────── */

export interface DirectedQuestionCardProps {
	question: DirectedQuestion
	index: number
	answerIndex: number
	responsibles: DirectedResponsible[]
	statusOnly?: boolean
}

export const DirectedQuestionCard = ({
	question,
	index,
	answerIndex,
	responsibles,
	statusOnly = false,
}: DirectedQuestionCardProps) => {
	const { t } = useTranslation()
	const {
		watch,
		setValue,
		control,
		formState: { errors },
	} = useFormContext<DirecionadaInput>()
	const baseName = `answers.${answerIndex}` as const
	const answerName = `${baseName}.answer` as const
	const responsibleName = `${baseName}.responsibleId` as const
	const dueDateName = `${baseName}.dueDate` as const
	const actionPlanName = `${baseName}.actionPlan` as const
	const statusName = `${baseName}.status` as const
	const answerImageName = `${baseName}.image` as const
	const answerImagePathName = `${baseName}.imagePath` as const
	const removeImageName = `${baseName}.removeImage` as const
	const answer = watch(answerName)
	const responsibleId = watch(responsibleName)
	const dueDate = watch(dueDateName)
	const actionPlan = watch(actionPlanName)
	const answerImage = watch(answerImageName)
	const answerImagePath = watch(answerImagePathName)
	const removeImage = watch(removeImageName)
	const [answerImagePreview, setAnswerImagePreview] = useState<string | null>(
		null,
	)
	const [showAnswerPhotoField, setShowAnswerPhotoField] = useState(false)
	const answerError = errors.answers?.[answerIndex]?.answer?.message
	const responsibleError = errors.answers?.[answerIndex]?.responsibleId?.message
	const dueDateError = errors.answers?.[answerIndex]?.dueDate?.message
	const actionPlanError = errors.answers?.[answerIndex]?.actionPlan?.message
	const statusError = errors.answers?.[answerIndex]?.status?.message
	const colors = INSPECTION_COLORS.oceanBlue
	const questionImageUrl = useMemo(() => {
		if (!question.imagePath) return null
		if (/^https?:\/\//i.test(question.imagePath)) return question.imagePath
		return question.imagePath
	}, [question.imagePath])
	const existingAnswerImageUrl = useMemo(() => {
		if (!answerImagePath || removeImage) return null
		if (/^https?:\/\//i.test(answerImagePath)) return answerImagePath
		return answerImagePath
	}, [answerImagePath, removeImage])
	const hasAnswerPhoto = Boolean(answerImagePreview ?? existingAnswerImageUrl)
	const responsibleLabel =
		responsibles.find((person) => person.id === responsibleId)?.name ?? '-'
	const dueDateLabel = dueDate ? formatDateToDisplay(dueDate) : '-'
	const actionPlanLabel = actionPlan?.trim() ? actionPlan : '-'

	useEffect(() => {
		if (!(answerImage instanceof File)) {
			setAnswerImagePreview(null)
			return
		}

		const preview = URL.createObjectURL(answerImage)
		setAnswerImagePreview(preview)
		return () => URL.revokeObjectURL(preview)
	}, [answerImage])

	useEffect(() => {
		if (hasAnswerPhoto) {
			setShowAnswerPhotoField(true)
		}
	}, [hasAnswerPhoto])

	const handleAnswerChange = (value: AnswerValue) => {
		setValue(`${baseName}.answer`, value, {
			shouldDirty: true,
			shouldValidate: true,
		})

		if (value !== 'NON_COMPLIANT') {
			setValue(`${baseName}.responsibleId`, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			})
			setValue(`${baseName}.dueDate`, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			})
			setValue(`${baseName}.actionPlan`, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			})
			setValue(`${baseName}.status`, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			})
		}
	}

	const handleAnswerPhotoChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const picked = e.target.files?.[0]
		if (!picked) return

		const file = await convertHeicIfNeeded(picked)
		setValue(answerImageName, file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setValue(removeImageName, false, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setShowAnswerPhotoField(true)
	}

	const handleRemoveAnswerPhoto = () => {
		setValue(answerImageName, undefined, {
			shouldDirty: true,
			shouldValidate: true,
		})

		if (answerImagePath) {
			setValue(removeImageName, true, {
				shouldDirty: true,
				shouldValidate: true,
			})
			setValue(answerImagePathName, undefined, {
				shouldDirty: true,
				shouldValidate: true,
			})
		}

		setShowAnswerPhotoField(false)
	}

	return (
		<div className="rounded-lg border border-kaefer-light-silver py-4 px-2 space-y-3">
			{/* Question header */}
			<div className="flex items-start gap-3">
				<span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-kaefer-ocean-blue/10 text-xs font-bold text-kaefer-ocean-blue">
					{index}
				</span>
				<p className="text-sm font-medium text-kaefer-dark-grey leading-snug pt-0.5">
					{question.title}
				</p>
			</div>

			{!statusOnly && questionImageUrl && (
				<div className="flex justify-center">
					<InspectionPhotoField
						attachLabel=""
						altText={t('inspections.directed.questionImageSelected')}
						preview={questionImageUrl}
						onFileChange={() => {}}
						onRemove={() => {}}
						colors={colors}
						showRemoveButton={false}
						showPickerWhenEmpty={false}
						hideOnPreviewError
					/>
				</div>
			)}

			{!statusOnly && (
				<FormField
					name={answerName}
					control={control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.directed.answersTitle')}
							error={typeof answerError === 'string' ? answerError : undefined}
						>
							<FieldContent fieldName={field.name}>
								<div className="grid grid-cols-3 gap-2">
									{ANSWER_OPTIONS.map((opt) => {
										const isSelected = field.value === opt.value
										return (
											<Button
												key={opt.value}
												type="button"
												variant="card-select"
												onClick={() => handleAnswerChange(opt.value)}
												className={cn(
													'text-xs sm:text-sm font-medium',
													isSelected && opt.selectedClass,
												)}
											>
												{t(opt.labelKey)}
											</Button>
										)
									})}
								</div>
							</FieldContent>
						</FormItem>
					)}
				/>
			)}

			{!statusOnly && (
				<div>
					{showAnswerPhotoField || hasAnswerPhoto ? (
						<FormItem
							label={t('inspections.directed.answerPhoto')}
							isRequired={false}
							error=""
						>
							<InspectionPhotoField
								attachLabel={t('inspections.directed.attachAnswerPhoto')}
								altText={t('inspections.directed.answerImageSelected')}
								preview={answerImagePreview ?? existingAnswerImageUrl}
								onFileChange={handleAnswerPhotoChange}
								onRemove={handleRemoveAnswerPhoto}
								colors={colors}
								compact
							/>
						</FormItem>
					) : (
						<Button
							type="button"
							variant="outline"
							onClick={() => setShowAnswerPhotoField(true)}
						>
							{t('inspections.directed.attachAnswerPhoto')}
						</Button>
					)}
				</div>
			)}

			{/* Action Plan — only when "Não Atende" */}
			{(statusOnly || answer === 'NON_COMPLIANT') && (
				<div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4 rounded-lg border border-kaefer-ocean-blue/20 bg-kaefer-ocean-blue/5 p-4">
					{statusOnly && (
						<div className="grid gap-3 sm:grid-cols-2">
							<div>
								<h4 className="mb-1 text-xs font-semibold text-kaefer-dark-grey">
									{t('inspections.directed.responsible')}
								</h4>
								<p className="text-sm text-kaefer-dark-grey">
									{responsibleLabel}
								</p>
							</div>
							<div>
								<h4 className="mb-1 text-xs font-semibold text-kaefer-dark-grey">
									{t('inspections.directed.deadline')}
								</h4>
								<p className="text-sm text-kaefer-dark-grey">{dueDateLabel}</p>
							</div>
							<div className="sm:col-span-2">
								<h4 className="mb-1 text-xs font-semibold text-kaefer-dark-grey">
									{t('inspections.directed.action')}
								</h4>
								<p className="text-sm text-kaefer-dark-grey whitespace-pre-wrap wrap-break-word">
									{actionPlanLabel}
								</p>
							</div>
						</div>
					)}

					{!statusOnly && (
						<h4 className="text-sm font-semibold text-kaefer-ocean-blue">
							{t('inspections.directed.actionPlanTitle')}
						</h4>
					)}

					{!statusOnly && (
						<FormField
							name={responsibleName}
							control={control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.directed.responsible')}
									error={
										typeof responsibleError === 'string'
											? responsibleError
											: undefined
									}
								>
									<FieldContent fieldName={field.name}>
										<SearchableSelect
											id={`responsible-${answerIndex}`}
											nameField={field.name}
											value={field.value}
											onValueChange={(value) => field.onChange(value)}
											options={
												responsibles?.map((p) => ({
													value: p.id,
													label: p.name,
												})) ?? []
											}
											placeholder={t('inspections.directed.selectResponsible')}
											searchPlaceholder={t('common.search')}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>
					)}

					{!statusOnly && (
						<>
							{/* Deadline */}
							<FormField
								name={dueDateName}
								control={control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.directed.deadline')}
										error={
											typeof dueDateError === 'string'
												? dueDateError
												: undefined
										}
									>
										<FieldContent fieldName={field.name}>
											<InspectionDateField
												value={
													field.value
														? parseStringToDateForDisplay(field.value)
														: undefined
												}
												onChange={(date) =>
													field.onChange(date ? formatDateYMD(date) : undefined)
												}
												isDueDate={true}
											/>
										</FieldContent>
									</FormItem>
								)}
							/>

							{/* Action description */}
							<FormField
								name={actionPlanName}
								control={control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.directed.action')}
										error={
											typeof actionPlanError === 'string'
												? actionPlanError
												: undefined
										}
									>
										<FieldContent fieldName={field.name}>
											<Textarea
												name={field.name}
												ref={field.ref}
												value={field.value ?? ''}
												onChange={(e) => field.onChange(e.target.value)}
												placeholder={t(
													'inspections.directed.actionPlaceholder',
												)}
												rows={3}
												className="resize-y bg-white"
											/>
										</FieldContent>
									</FormItem>
								)}
							/>
						</>
					)}

					{statusOnly && hasAnswerPhoto && (
						<div>
							<h4 className="mb-2 text-xs font-semibold text-kaefer-dark-grey">
								{t('inspections.directed.answerPhoto')}
							</h4>
							<InspectionPhotoField
								attachLabel=""
								altText={t('inspections.directed.answerImageSelected')}
								preview={answerImagePreview ?? existingAnswerImageUrl}
								onFileChange={() => {}}
								onRemove={() => {}}
								colors={colors}
								showRemoveButton={false}
								showPickerWhenEmpty={false}
								hideOnPreviewError
							/>
						</div>
					)}

					{/* Status */}
					<FormField
						name={statusName}
						control={control}
						render={({ field }) => (
							<FormItem
								label={t('inspections.directed.status')}
								error={
									typeof statusError === 'string' ? statusError : undefined
								}
							>
								<FieldContent fieldName={field.name}>
									<div className="grid grid-cols-2 gap-2">
										{STATUS_OPTIONS.map((opt) => {
											if (statusOnly && opt.value === 'IN_PROGRESS') {
												return field.value === 'IN_PROGRESS' ? (
													<Button
														key={opt.value}
														type="button"
														variant="card-select"
														onClick={() => field.onChange(opt.value)}
														className={cn(
															'px-3 py-2.5 text-sm font-medium',
															'bg-kaefer-ocean-blue text-white hover:bg-kaefer-ocean-blue/90 border-kaefer-ocean-blue',
														)}
													>
														{t(opt.labelKey)}
													</Button>
												) : null
											}
											const isSelected = field.value === opt.value
											return (
												<Button
													key={opt.value}
													type="button"
													variant="card-select"
													onClick={() => field.onChange(opt.value)}
													className={cn(
														'px-3 py-2.5 text-sm font-medium',
														isSelected &&
															'bg-kaefer-ocean-blue text-white hover:bg-kaefer-ocean-blue/90 border-kaefer-ocean-blue',
													)}
												>
													{t(opt.labelKey)}
												</Button>
											)
										})}
									</div>
								</FieldContent>
							</FormItem>
						)}
					/>
				</div>
			)}
		</div>
	)
}
